/**
 * StaffSentList — recently sent + scheduled announcements card.
 *
 * Click a sent row to open the AnnouncementThread modal (parent component
 * passes `onOpenThread`). Scheduled rows render a Cancel link instead.
 */
import { Clock, CheckCircle2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString();
}

export default function StaffSentList({ items, onOpenThread, onCancelScheduled }) {
  if (!items?.length) return null;
  return (
    <div
      data-testid="staff-sent-messages"
      className="mt-5 rounded-2xl border hairline bg-white p-4"
    >
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863] mb-3">
        Recently sent & scheduled
      </div>
      <ol className="space-y-2" data-testid="staff-sent-list">
        {items.slice(0, 6).map((m) => (
          <li
            key={m.id}
            data-testid={`staff-sent-row-${m.id}`}
            data-reply-mode={m.reply_mode || "broadcast"}
            className="flex items-center gap-3 flex-wrap"
          >
            {m.is_scheduled ? (
              <Clock size={11} strokeWidth={1.5} className="text-[#A89CC8]" />
            ) : (
              <CheckCircle2
                size={11}
                strokeWidth={1.5}
                className="text-[#8A9984]"
              />
            )}
            <button
              type="button"
              onClick={() => !m.is_scheduled && onOpenThread(m)}
              data-testid={`staff-sent-row-${m.id}-open`}
              className={
                "font-serif text-sm text-[#2C2B29] truncate flex-1 min-w-0 text-left " +
                (m.is_scheduled ? "cursor-default" : "hover:underline")
              }
              disabled={m.is_scheduled}
            >
              {m.title}
            </button>
            {m.reply_mode && m.reply_mode !== "broadcast" && (
              <span
                className="text-[10px] tracking-[0.18em] uppercase"
                style={{
                  color: m.reply_mode === "quiet" ? "#1A1B2E" : "#8A9984",
                }}
                data-testid={`staff-sent-row-${m.id}-mode`}
              >
                {m.reply_mode === "quiet" ? "Quiet" : "Open"}
                {(m.reply_count || 0) > 0 && (
                  <span data-testid={`staff-sent-row-${m.id}-replies`}>
                    {" · "}
                    {m.reply_count}
                  </span>
                )}
              </span>
            )}
            <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
              {m.target_group_name || "All groups"}
            </span>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
              {m.is_scheduled
                ? `→ ${new Date(m.scheduled_for).toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}`
                : timeAgo(m.created_at)}
            </span>
            {m.is_scheduled && (
              <button
                type="button"
                onClick={() => onCancelScheduled(m.id)}
                data-testid={`staff-sent-cancel-${m.id}`}
                className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863] hover:text-[#2C2B29] underline"
              >
                Cancel
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
